import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import PropType from "prop-types";
import { bindActionCreators } from "redux";

import {
  Popover, IconButton, Typography, Grid, Container, List,
  Divider, ListItem, ListItemText, ListItemSecondaryAction, ListItemAvatar, Link,
  Collapse, TextField, CircularProgress, Input, Accordion, AccordionSummary,
  AccordionDetails, Tooltip, InputAdornment,
} from "@material-ui/core";
import {
  ShoppingCart, AddShoppingCart, Cancel, CheckCircle, Add,
  Edit, AccountBalanceWallet, AttachMoney
} from "@material-ui/icons";
import { Autocomplete, Alert } from "@material-ui/lab";
import { withStyles } from "@material-ui/core/styles";

import * as PaymentActions from "../actions/PaymentActions";
import * as CartActions from "../actions/CartActions";

import {
  createOrder, addProductToOrder, mapForCart, removeProductFromOrder, formatPrice, hasCustomOfferWithSubscription,
  removeAllItemsWithSubs, getTotalPricesStrArray, isCouponCodeAllowed, addCouponCode, removeCouponCode, addInstallment,
  removeInstallment, validToAddSubscriptionPlan, onReturnKeyPress, removeRebate, removeTradingBalance, removeXMarketWallet,
} from "../utils";
import { QUANTITY_REQUIRED_PRODUCTS, CartItemTypes, SubIntervalMap, ItemPaidStatusTypes, FAIR_MARKET } from "../constants";

const styles = (theme) => ({
  cartIcon: {
    color: "white",
  },
  cartHeader: {
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: "6px",
    marginTop: "10px",
  },
  listItemRoot: {
    width: "85%",
    paddingRight: "0",
  },
  listItemAvatarRoot: {
    marginRight: "16px",
    minWidth: "unset",
  },
  customOfferSummaryRoot: {
    padding: '0px',
    minHeight: 'unset'
  },
  customOfferSummaryContent: {
    margin: '0px'
  },
  customOfferSummaryExpanded: {
    margin: '0px !important',
    minHeight: 'unset !important',
  },
  customOfferDetails: {
    padding: '0px'
  },
  customOfferListRoot: {
    padding: '0px',
    width: '100%',
  },
  customOfferListItemRoot: {
    padding: '0px 16px'
  },

  paymentPlanLabels: {
    fontWeight: 'bold',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'max-content',
    placeContent: 'center',
    fontSize: '0.9em'
  },
  
  paymentPlanValues: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'max-content',
    placeContent: 'center',
    fontSize: '0.9em'
  },
  paymentPlanInstallmentContainer: {
    fontSize: '1.2em',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'max-content',
    placeContent: 'center',
    fontWeight: 'bold',
  },
  MuiListPadding: {
    paddingTop: '0px',
  }
});

// Cart Icon with Price
const CartIcon = withStyles({
  label: {
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: "6px",
    color: "white",
  },
})(IconButton);

const AddToCartIcon = withStyles({
  root: {
    padding: "5px",
  },
})(IconButton);

class Cart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      cartAnchorEl: null,
      cartOpen: false,
      addItem: false,
      couponCodeOpen: false,
      coupon: "",
      isCheckingCoupon: false,
      addedProduct: "",
      cartIsLoading: false,
      paymentPlanValue: '',
      paymentPlanError: '',
      isPaymentPlanLoading: false,
    };

    this.couponTextInput = React.createRef();
    this.addProductTextInput = React.createRef();
    this.paymentPlanTextInput = React.createRef(null);

    this.handleCartOpen = this.handleCartOpen.bind(this);
    this.handleCartClose = this.handleCartClose.bind(this);
    this.toggleAddItem = this.toggleAddItem.bind(this);
    this.toggleOpenCoupon = this.toggleOpenCoupon.bind(this);
    this.onApplyCouponCode = this.onApplyCouponCode.bind(this);
    this.onCouponInputChange = this.onCouponInputChange.bind(this);
    this.onClearCoupon = this.onClearCoupon.bind(this);
    this.onClearRebate = this.onClearRebate.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.setCartLoading = this.setCartLoading.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.cartOpen && prevState.cartOpen) this.setState({ addItem: false });

    if (this.props.coupon === null && prevProps.coupon) this.setState({ paymentPlanValue: '', paymentPlanError: '' });
  }

  handleCartOpen = (e) => this.setState({ cartAnchorEl: e.currentTarget, cartOpen: true });

  handleCartClose = () => this.setState({ cartAnchorEl: null, cartOpen: false });

  setCartLoading = (cartIsLoading) => this.setState({ cartIsLoading });

  toggleAddItem() {
    this.setState({ addItem: !this.state.addItem, couponCodeOpen: false }, () => {
      if(this.state.addItem) this.addProductTextInput.current.focus();
    });
  }

  toggleOpenCoupon = () => 
    this.setState({ couponCodeOpen: !this.state.couponCodeOpen, addItem: false }, () => {
      if(this.state.couponCodeOpen) this.couponTextInput.current.focus();
    });

  calculateTotalPrice = () => this.props.cart.reduce((total, { price, paidFor }) => !paidFor ? total + price : total , 0);
  
  onCouponInputChange = (coupon) => this.setState({ coupon });

  onPaymentPlanChange = (paymentPlanValue) => this.setState({ paymentPlanValue });

  validatePaymentPlanValue = () => {
    const { state, props } = this;
    const { paymentPlanValue } = state;
    const { coupon, actions, cart } = props;
    let totalPrice = cart.reduce((total, { price }) => total += price, 0);
    let paymentPlanError = '';

    if (!coupon || !coupon.plan || !coupon.plan._id) {
      paymentPlanError = 'Something went wrong';
      actions.openSnackbar('error', 'Something went wrong');
    }

    const { minAmount } = coupon.plan;
    const { discount } = coupon;

    if (discount && discount > 0) {
      totalPrice -= (totalPrice * discount) / 100;
    }
    totalPrice = Number(totalPrice.toFixed(2));

    if (isNaN(Number(paymentPlanValue))) {
      paymentPlanError = 'Invalid Payment amount';
      actions.openSnackbar('error', 'Invalid Payment amount');
    }

    if (Number(paymentPlanValue) < minAmount) {
      paymentPlanError = `Payment value must not be less than $${formatPrice(minAmount)}`;
      actions.openSnackbar('error', `Payment value must not be less than $${formatPrice(minAmount)}`);
    }
    
    if (Number(paymentPlanValue) > totalPrice) {
      paymentPlanError = `Payment value must not be more than $${formatPrice(totalPrice)}`;
      actions.openSnackbar('error', `Payment value must not be more than $${formatPrice(totalPrice)}`);
    }

    if (paymentPlanError.length > 0) {
      this.setState({ paymentPlanValue: `${minAmount}`, paymentPlanError });
      this.paymentPlanTextInput.current.focus();
      return false;
    }

    return true;
  }

  makeProducts = (products) => {
    if (!products) return [];
    else return products;
  };

  onApplyCouponCode = async () => {
    let { coupon } = this.state;

    if(coupon === "")
      return this.props.actions.openSnackbar('error', 'Please add a coupon code');
      
      let { cartActions,  actions } = this.props;

    this.setState({ isCheckingCoupon: true });

    try {
      const { order } = await addCouponCode(coupon);

      if (order) {
        actions.openSnackbar('success', 'Coupon Applied');

        cartActions.setCart({ ...order, items: order.items.map(mapForCart) });
      } else throw new Error('Unknown Error');
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Error');
    } finally {
      this.setState({ couponCodeOpen: false, isCheckingCoupon: false, coupon: '' });
    }
  }

  onAddInstallment = async () => {
    const { actions, cartActions } = this.props;
    const { paymentPlanValue } = this.state;
    if(!this.validatePaymentPlanValue()) return;

    this.setState({ isPaymentPlanLoading: true });

    try {

      const { order } = await addInstallment(Number(paymentPlanValue));

      if (order.installments) {
        cartActions.setInstallments(order.installments)
        actions.openSnackbar('success', 'Payment Updated');
      } else throw new Error('error', 'Something went wrong');
      
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Something went wrong');
    } finally {
      this.setState({ isPaymentPlanLoading: false });
    }
  }

  onRemoveInstallment = async () => {
    const { actions, cartActions } = this.props;
    this.setState({ isPaymentPlanLoading: true });

    try {
      const { order } = await removeInstallment();

      if (order.installments) {
        cartActions.setInstallments(order.installments)
        actions.openSnackbar('success', 'Payment Updated');
      } else throw new Error('error', 'Something went wrong');
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Something went wrong')
    } finally {
      this.setState({ isPaymentPlanLoading: false });
    }
  }

  onReturnToApplyCouponCode = (event) => onReturnKeyPress(event, this.onApplyCouponCode)

  onClearCoupon = async () => {
    const { props, setCartLoading } = this;
    const { actions, cartActions } = props;

    try {
      setCartLoading(true);

      const { order } = await removeCouponCode();

      if (order) {
        actions.openSnackbar('success', 'Coupon removed');

        cartActions.setCoupon(null)
      } else throw new Error('Unknown Error');

    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Error');
    } finally {
      setCartLoading(false);
    }
  }

  onClearRebate = async () => {
    const { props, setCartLoading } = this;
    const { actions, cartActions } = props;

    try {
      setCartLoading(true);

      const { order } = await removeRebate();
      if (order) {
        actions.openSnackbar('success', 'Rebate removed');

        cartActions.setRebate(0)
      } else throw new Error('Unknown Error');

    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Error');
    } finally {
      setCartLoading(false);
    }
  }

  onClearTradingBalance= async () => {
    const { props, setCartLoading } = this;
    const { actions, cartActions } = props;

    try {
      setCartLoading(true);

      const { order } = await removeTradingBalance();
      if (order) {
        actions.openSnackbar('success', 'applied trading balance removed');

        cartActions.setBalance({ tradingAccount: 0, balance: 0 })
      } else throw new Error('Unknown Error');

    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Error');
    } finally {
      setCartLoading(false);
    }
  }

  onClearDepositAndCredit = async () => {
    const { props, setCartLoading } = this;
    const { actions, cartActions } = props;

    try {
      setCartLoading(true);

      const { order } = await removeXMarketWallet();
      if (order) {
        actions.openSnackbar('success', 'X-Market wallet removed');

        cartActions.setDeposit(0);
        cartActions.setCredit(0);
      } else throw new Error('Unknown Error');

    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Error');
    } finally {
      setCartLoading(false);
    }
  }

  onQuantityChange = (value, productId) => {
    let message;

    if (isNaN(Number(value))) message = "Quantity must be a number";
    else if (Number(value) <= 0 || Number(value) > 100) message = "Quantity must be a number between 1-100";

    if (message) this.props.actions.openSnackbar('error', message);

    this.props.actions.updateProductDetail({ id: productId, changes: { quantity: value }  });
  }

  addProduct = async (item) => {
    if (!item) return;

    const { props, setCartLoading } = this;
    const { actions, cart, cartActions, rebate, coupon } = props;
    const { value: product, label: productName } = item;
    const items = [product];
    let result;
    if (!validToAddSubscriptionPlan(rebate, coupon, product)) {
      actions.openSnackbar('error', `Failed to Add "${productName}" to your cart, Please remove coupon or rebate before adding subscription plan`);
      return;
    }
    // if (item && item.onModel === )

    this.toggleAddItem();

    try {
      setCartLoading(true);
      if (Array.isArray(cart) && cart.length === 0) {
        result = await createOrder({ items });
      } else {
        result = await addProductToOrder({ items });
      }
      const { order } = result;

      if (order) {
        cartActions.setCart({ ...order, items: order.items.map(mapForCart) });
        actions.openSnackbar('success', `Added "${productName}" to your cart`);
      } else actions.openSnackbar('error', `Failed to Add "${productName}" to your cart`);
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Cant add product to cart');
    } finally {
      setCartLoading(false);
    }
  }

  removeProduct = async (item) => {
    if (!item) return;

    const { props, setCartLoading } = this;
    const { actions, cartActions } = props;
    const items = [item];
    
    try {
      setCartLoading(true);
      let result = await removeProductFromOrder({ items });
      const { order } = result;

      if (order) {
        cartActions.setCart({ ...order, items: order.items.map(mapForCart) });
        actions.openSnackbar('success', `Removed from your cart`);
      } else cartActions.resetCart();
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Cannot remove product from cart');
    } finally {
      setCartLoading(false);
    }
  }

  renderProductTitle = (product) => {
    const { onQuantityChange } = this;
    if(QUANTITY_REQUIRED_PRODUCTS.includes(product.shoping_product_id)) {
      return (<div style={{ display: "grid", gridTemplateColumns: "1fr max-content" }}>
        <Typography variant="body1" noWrap={true} style={{ fontWeight: "400" }} >{product.shoping_product_name}</Typography>
        <div style={{ width: "100px" }}>
          <Input type="number" placeholder="Quantity" value={product.quantity} onChange={(e) => onQuantityChange(e.target.value, product.shoping_product_id)} />
        </div>
      </div>);
    } else return product.shoping_product_name;
  }

  renderCustomOfferList = (items = [], paidFor) => {
    const { customOfferSummaryRoot, customOfferSummaryContent, customOfferSummaryExpanded, customOfferDetails, customOfferListRoot, customOfferListItemRoot } = this.props.classes;

    return (
      <Accordion>
        <AccordionSummary classes={{ root: customOfferSummaryRoot, content: customOfferSummaryContent, expanded: customOfferSummaryExpanded }}>
          <Link>Details</Link>
        </AccordionSummary>
        <AccordionDetails classes={{ root: customOfferDetails }}>
          <List dense={true} classes={{ root: customOfferListRoot }}>
          {items.map(({ name, interval, price, _id }, i) => {
            let priceString = interval ? `$${formatPrice(price)}/${SubIntervalMap[interval].short} (first ${SubIntervalMap[interval].display} free)` : `$${formatPrice(price)}`;
            let paidStatus = `${interval && paidFor.includes(_id) ? '(Paid)': ''}`;
            return (<Fragment key={i}>
              <Divider orientation="horizontal"/>
  
              <ListItem classes={{ root: customOfferListItemRoot }}>
                <ListItemText primary={name} secondary={paidStatus} />
                <ListItemSecondaryAction>{priceString}</ListItemSecondaryAction>
              </ListItem>
            </Fragment>)
          })}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  }

  renderCartList = () => {
    const { state, props, removeProduct } = this;
    const { classes, cart } = props;

    const cartItems = cart || [];
    let isFairMarkets = false;

    if (state.cartIsLoading) return (
      <ListItem style={{ display: 'grid', gridTemplateColumns: "max-content", placeContent: 'center' }}>
        <CircularProgress size={40} />
      </ListItem>
    );

    if (cartItems.length <= 0) return (
      <ListItem>
        <ListItemText primary="Cart Empty" primaryTypographyProps={{ variant: "h6", noWrap: true, style: { textAlign: "center" } }} />
      </ListItem>
    );
    else return cartItems.map(({ _id, name, price, interval, type, paidFor, items }, i) => {
      let itemPrice = `$${formatPrice(price)}`;
      let itemPaidStatus = ItemPaidStatusTypes.none;

      if (type !== CartItemTypes.customOffer) {
        if (paidFor.includes(_id)) {
          itemPrice = 'Paid';
          itemPaidStatus = ItemPaidStatusTypes.full;
        }
      } else {
        let customOfferSubIds = items.map(({ type, _id }) => type === CartItemTypes.subscriptionPlan ? _id : undefined).filter(x => x);
        if (paidFor.includes(customOfferSubIds[0]) && paidFor.includes(_id)) {
          itemPaidStatus = ItemPaidStatusTypes.full;
          itemPrice = 'Fully Paid';
        } else if (paidFor.includes(customOfferSubIds[0]) || paidFor.includes(_id)) {
          itemPaidStatus = ItemPaidStatusTypes.half;
          itemPrice = 'Partially Paid';
        } else itemPaidStatus = ItemPaidStatusTypes.none;
      }

      if (_id === FAIR_MARKET) {
        isFairMarkets = true;
      }

      return (
        <div key={`item-${i}`}>
          <Divider orientation="horizontal"/>
          <ListItem classes={{ root: classes.listItemRoot }}>
            <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>{((status) => {
              if (status === ItemPaidStatusTypes.none) return (
                <Tooltip title="Remove Item" placement="bottom">
                  <Link onClick={(e) => removeProduct({ modelId: _id, onModel: type })} aria-label="remove-item" >
                    <Cancel style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
                  </Link> 
                </Tooltip>
              ); else if (status === ItemPaidStatusTypes.half) return (
                <Tooltip title={itemPrice} placement="bottom">
                  <Link aria-label="paid-item" >
                    <CheckCircle style={{ color: "#ffc400" }} fontSize="large" />
                  </Link>
                </Tooltip>
              ); else if (status === ItemPaidStatusTypes.full) return (
                <Tooltip title={itemPrice} placement="bottom">
                  <Link aria-label="paid-item" >
                    <CheckCircle style={{ color: "#4caf50" }} fontSize="large" />
                  </Link>
                </Tooltip>
              );
            })(itemPaidStatus)}</ListItemAvatar>
            <ListItemText
              primary={name}
              secondary={type === CartItemTypes.customOffer ? this.renderCustomOfferList(items, paidFor) : interval ?  isFairMarkets ? `$${formatPrice(price)}/3 ${SubIntervalMap[interval].display}s` : `$${formatPrice(price)}/${SubIntervalMap[interval].display}` : `$${formatPrice(price)}`}
              primaryTypographyProps={{ variant: "body1", noWrap: true }}
              secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
            />
            <ListItemSecondaryAction>
              <Typography variant="body2">{itemPrice}</Typography>
            </ListItemSecondaryAction>
          </ListItem>
        </div>
        )
      }
    );
  }

  renderProductListSelect = () => {
    const { props, addProduct } = this;
    const { searchableProducts, cart } = props;
    const hasSubscription = cart.filter(({ type }) => type === CartItemTypes.subscriptionPlan).length > 0 || hasCustomOfferWithSubscription(cart);

    let options = searchableProducts;

    if (hasSubscription) options = removeAllItemsWithSubs(searchableProducts);

    return (<Autocomplete
      value={null}
      options={options}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => option.groupBy}
      onChange={(e, v, r) => addProduct(v)}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          fullWidth
          label="Add Product"
          placeholder="Select a product"
          onChange={console.log}
          inputRef={this.addProductTextInput}
        />
      )}
    />)
  }

  renderAddCouponLink = () => {
    const { props, toggleOpenCoupon } = this;

    if (!isCouponCodeAllowed(props.cart || [], props.rebate, props.creditUsed)) return null;
    else return (
      <Container style={{ paddingTop: "3px" }}>
        <Link style={{ color: "#929292", cursor: "pointer" }} onClick={toggleOpenCoupon}>Apply Coupon Code &#8250;</Link>
      </Container>
    );
  }

  renderPaymentPlan = (coupon, installments = []) => {
    const { state, props, onPaymentPlanChange, onAddInstallment, onRemoveInstallment } = this;
    const { paymentPlanValue, isPaymentPlanLoading } = state;
    const { paymentPlanLabels, paymentPlanInstallmentContainer } = props.classes;
    const unpaidInstallment = installments.find(({ paid }) => !paid);
    const createdAt = new Date(props.orderCreatedAt);

    let paymentPlanDeadline = null;
    if (createdAt instanceof Date) {
      paymentPlanDeadline = createdAt.setDate(createdAt.getDate() + coupon.plan.lifeSpan);
    }

    return (
      <div style={{ display: 'grid', gridTemplateRows: 'repeat(4, max-content)', gridTemplateColumns: '1fr' }}>
        <div style={{ display: 'grid', gridTemplateRows: '1fr', gridTemplateColumns: 'max-content 1fr', gridGap: '5px' }}>
          <Typography variant="caption" classes={{ root: paymentPlanLabels }}>Minimum Payment:</Typography>
          <Typography variant="caption">${formatPrice(coupon.plan.minAmount)}</Typography>
        </div>
        
        <div style={{ display: 'grid', gridTemplateRows: '1fr', gridTemplateColumns: 'max-content 1fr', gridGap: '5px' }}>
          <Typography variant="caption" classes={{ root: paymentPlanLabels }}>Deadline:</Typography>
          <Typography variant="caption">{paymentPlanDeadline? new Date(paymentPlanDeadline).toLocaleDateString() : 'Date Missing'}</Typography>
        </div>

        {!unpaidInstallment ?
        <div>
          <TextField
            placeholder="How much would you like to pay?"
            fullWidth
            value={paymentPlanValue}
            label="Payment"
            type="number"
            focused={true}
            autoComplete="off"
            inputRef={this.paymentPlanTextInput}
            onChange={(e) => onPaymentPlanChange(e.target.value)}
            onKeyDown={(e) => onReturnKeyPress(e, onAddInstallment)}
            InputProps={{
              startAdornment:
                <InputAdornment position="start">
                  <AttachMoney style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
                </InputAdornment>,
              endAdornment:
                <InputAdornment position="start">
                  <IconButton onClick={onAddInstallment}>
                  {isPaymentPlanLoading ? <CircularProgress size={20} /> : <Add />}
                  </IconButton>
                </InputAdornment>,
            }}
         />
        </div>
        :
        <div style={{ display: 'grid', gridTemplateRows: '1fr', gridTemplateColumns: 'max-content 1fr max-content', gridGap: '5px' }}>
          <Typography variant="caption" classes={{ root: paymentPlanInstallmentContainer }}>Paying:</Typography>
          <Typography variant="caption" classes={{ root: paymentPlanInstallmentContainer }}>${formatPrice(unpaidInstallment.amount)}</Typography>
          <IconButton onClick={onRemoveInstallment}>
            {isPaymentPlanLoading ? <CircularProgress size={20} /> : <Edit />}
          </IconButton>
        </div>

        }
      </div>
    );
  }

  renderDiscountListItem = () => {
    const { props, onClearCoupon, renderPaymentPlan, onClearRebate, onClearTradingBalance, onClearDepositAndCredit } = this;
    const { coupon, classes, cart, installments, rebate, balance, depositUsed, creditUsed } = props;
    const content = [];

    if (!coupon || coupon.discount === undefined || coupon.discount === null) content.push(null);
    else {
      const hasPlan = coupon.plan && coupon.plan._id;
      const totalPrice = cart.reduce((total, { price }) => total += price, 0);
      const discountedPrice = coupon.discount > 0 ? (totalPrice * coupon.discount)  / 100 : 0;

      content.push(<Divider key="discount-1" orientation="horizontal" />);

      content.push(
        <ListItem key="discount-2" classes={{ root: classes.listItemRoot }}>
          <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
            <Link aria-label="delete" style={{ cursor: "pointer" }} onClick={onClearCoupon}>
              <Cancel style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
            </Link>
          </ListItemAvatar>
          <ListItemText
            primary="Applied Coupon"
            secondary={`${coupon.discount}% discount ${hasPlan ? '+ Payment Plan option' : ''}`}
            primaryTypographyProps={{ variant: "body1", noWrap: true }}
            secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
          />
          <ListItemSecondaryAction style={{ top: "34%" }}>
            <Typography variant="body2">-${formatPrice(discountedPrice)}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
      );
      
      if (hasPlan) {
        content.push(
          <ListItem key="discount-3" classes={{ root: classes.listItemRoot }}>
            <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
                <AccountBalanceWallet style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
            </ListItemAvatar>
            <ListItemText
              primary="Payment Plan"
              secondary={renderPaymentPlan(coupon, installments)}
              primaryTypographyProps={{ variant: "body1", noWrap: true }}
              secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
            />
            <ListItemSecondaryAction style={{ top: "34%" }}>
              <Typography variant="body2">-${formatPrice(discountedPrice)}</Typography>
            </ListItemSecondaryAction>
          </ListItem>
        );
      }
    }

    if (Number(rebate) && Number(rebate) > 0) {
      content.push(<Divider key="discount-4" orientation="horizontal" />);
      content.push(
        <ListItem key="discount-5" classes={{ root: classes.listItemRoot }}>
          <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
            <Link aria-label="delete" style={{ cursor: "pointer" }} onClick={onClearRebate}>
              <Cancel style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
            </Link>
          </ListItemAvatar>
          <ListItemText
            primary="Rebate Used"
            secondary={`discount`}
            primaryTypographyProps={{ variant: "body1", noWrap: true }}
            secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
          />
          <ListItemSecondaryAction style={{ top: "34%" }}>
            <Typography variant="body2">-${formatPrice(rebate)}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
      );
    }

    if (Number(balance) && Number(balance) > 0) {
      content.push(<Divider key="discount-6" orientation="horizontal" />);
      content.push(
        <ListItem key="discount-7" classes={{ root: classes.listItemRoot }}>
          <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
            <Link aria-label="delete" style={{ cursor: "pointer" }} onClick={onClearTradingBalance}>
              <Cancel style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
            </Link>
          </ListItemAvatar>
          <ListItemText
            primary="Trading Balance Used"
            secondary={`discount`}
            primaryTypographyProps={{ variant: "body1", noWrap: true }}
            secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
          />
          <ListItemSecondaryAction style={{ top: "34%" }}>
            <Typography variant="body2">-${formatPrice(balance)}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
      );
    }

    if ((Number(depositUsed) && Number(depositUsed) > 0) || (Number(creditUsed) && Number(creditUsed) > 0)) {
      content.push(<Divider key="discount-8" orientation="horizontal" />);
      content.push(
        <ListItem key="discount-9" classes={{ root: classes.listItemRoot }}>
          <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
            <Link aria-label="delete" style={{ cursor: "pointer" }} onClick={onClearDepositAndCredit}>
              <Cancel style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
            </Link>
          </ListItemAvatar>
          <ListItemText
            primary="X-Market Wallet"
            secondary={`discount`}
            primaryTypographyProps={{ variant: "body1", noWrap: true }}
            secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
          />
          <ListItemSecondaryAction style={{ top: "34%" }}>
            <Typography variant="body2">-${formatPrice(depositUsed + creditUsed)}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
      );
    }

    return content;
  }

  renderApplyCouponListItem = () => {
    const { state, onCouponInputChange, onReturnToApplyCouponCode, onApplyCouponCode } = this;
    const { isCheckingCoupon, coupon } = state;

    return (
      <ListItem>
        <ListItemText
          primary={
            <TextField
              placeholder="Enter your coupon code"
              fullWidth
              label="Coupon Code"
              autoComplete="off"
              value={coupon}
              inputRef={this.couponTextInput}
              onChange={(e) => onCouponInputChange(e.target.value)}
              disabled={isCheckingCoupon}
              onKeyDown={onReturnToApplyCouponCode}
            />
          }
        />

        <ListItemSecondaryAction>
          <IconButton edge="end" aria-label="apply" onClick={onApplyCouponCode}>
            {isCheckingCoupon ? <CircularProgress size={20} /> : <Add />}
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  renderCartTotalListItem = () => {
    const { props } = this;
    const { cart, coupon, installments, step, rebate, balance, depositUsed, creditUsed } = props;
    const installment = installments.find(({ paid }) => !paid);
    const content = [];
    const [ productTotalStr, customOfferSubsTotalStr, subsTotalStr ] = getTotalPricesStrArray(cart, coupon, installment, rebate, balance, depositUsed, creditUsed);
    const hasSubscription = subsTotalStr.length > 0 || customOfferSubsTotalStr.length > 0;

    if (productTotalStr.length > 0) {
      content.push(
        <Typography key='1' variant="h6" noWrap>
          <strong>{productTotalStr}</strong>
        </Typography>
      );
    }

    if (subsTotalStr.length > 0) {
      content.push(
        <Typography key='0' variant="h6" noWrap>
          <strong>{subsTotalStr}</strong>
        </Typography>
      );
    }

    if (customOfferSubsTotalStr.length > 0) {
      content.push(
        <Typography key='0' variant="h6" noWrap>
          <strong>{customOfferSubsTotalStr}</strong>
        </Typography>
      );
    }

    if (content.length === 0) content.push(
      <Typography key='2' variant="h6" noWrap>
        <strong>$0</strong>
      </Typography>
    );

    return (<>
      <ListItem>
        <ListItemText primary={<Typography variant="h6" noWrap>Total:</Typography>} />

        <ListItemSecondaryAction style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gridGap: '5px' }} >
          {content}
        </ListItemSecondaryAction>
      </ListItem>
      {!hasSubscription ? null :
      <ListItem>
        <ListItemText primary={
          <Alert variant="outlined" severity="info">If paying with PayPal, subscriptions are a separate transaction.</Alert>
        } />
      </ListItem>
      }
      {step !== 4 ? null : 
        <ListItemText primary={
          <Alert variant="outlined" severity="warning">You are Paying for an installment and not for this cart</Alert>
        } />
      }
    </>);
  }

  render = () => {
    const {
      props, state,
      handleCartClose, handleCartOpen, toggleAddItem,
    } = this;
    const { cartOpen, cartAnchorEl, addItem, couponCodeOpen } = state;
    const { classes, cart, coupon, installments, pageView, rebate, balance, depositUsed, creditUsed } = props;
    const installment = installments.find(({ paid }) => !paid);
    const totalPriceArray = getTotalPricesStrArray(cart, coupon, installment, rebate, balance, depositUsed, creditUsed).slice(0, -1);

    if (pageView) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ paddingTop: "0" }}>
            <List dense={true}>
              {this.renderCartList()}
              {this.renderDiscountListItem()}

              <Divider orientation="horizontal" />
              <Collapse in={addItem}>
                <ListItem>
                  <ListItemText
                    primary={this.renderProductListSelect()}
                  />
                </ListItem>
              </Collapse>

              <Collapse in={couponCodeOpen}>
                {this.renderApplyCouponListItem()}
              </Collapse>

              {this.renderCartTotalListItem()}
            </List>
          </Grid>
        </Grid>
      )
    }

    return (
      <div>
        <CartIcon onClick={handleCartOpen}>
          <Typography variant="h6">{totalPriceArray.join(' ')}</Typography>
          <ShoppingCart />
        </CartIcon>
        <Popover
          open={cartOpen}
          anchorEl={cartAnchorEl}
          onClose={handleCartClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ paddingBottom: "0" }}>
              <Container className={classes.cartHeader}>
                <Typography variant="h5" noWrap style={{ fontWeight: "500", lineHeight: "2" }}>Your shopping cart</Typography>
                <AddToCartIcon onClick={toggleAddItem}>
                  <AddShoppingCart style={{ fontSize: "1.9rem"}} />
                </AddToCartIcon>
              </Container>
              {this.renderAddCouponLink()}
            </Grid>

            <Grid item xs={12}>
              <List dense={true}>
                {this.renderCartList()}
                {this.renderDiscountListItem()}

                <Divider orientation="horizontal" />
                <Collapse in={addItem}>
                  <ListItem>
                    <ListItemText
                      primary={this.renderProductListSelect()}
                    />
                  </ListItem>
                </Collapse>

                <Collapse in={couponCodeOpen}>
                  {this.renderApplyCouponListItem()}
                </Collapse>

                {this.renderCartTotalListItem()}
              </List>
            </Grid>
          </Grid>
        </Popover>
      </div>
    );
  };
}

Cart.propTypes = {
  classes: PropType.object.isRequired,
};

const mapStateToProps = ({ payment, cartReducer }) => ({
  step: payment.step,
  searchableProducts: payment.searchableProducts,
  cart: cartReducer.cart,
  coupon: cartReducer.coupon,
  installments: cartReducer.installments,
  totalPrice: cartReducer.totalPrice,
  orderCreatedAt: cartReducer.orderCreatedAt,
  rebate: cartReducer.rebate,
  balance: cartReducer.balance,
  depositUsed: cartReducer.depositUsed,
  creditUsed: cartReducer.creditUsed,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(PaymentActions, dispatch),
  cartActions: bindActionCreators(CartActions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Cart));
